import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import PartnerLogos from "@/components/shared/PartnerLogos";
import CTASection from "@/components/shared/CTASection";
import PageHero from "@/components/shared/PageHero";
import VibrantSectionHeader from "@/components/shared/VibrantSectionHeader";
import PageSEO from "@/components/shared/PageSEO";
import Breadcrumbs from "@/components/shared/Breadcrumbs";
import { Button } from "@/components/ui/button";
import { 
  Leaf, 
  Target, 
  Eye, 
  Users, 
  Award, 
  Heart,
  TreePine,
  Zap,
  MapPin,
  ArrowRight,
  Calendar,
  Lightbulb,
  Shield,
  CheckCircle2,
  BookOpen,
  Rocket,
  Building2,
  TrendingUp,
  Globe
} from "lucide-react";
import { Link } from "react-router-dom";
import ceoPhoto from "@/assets/ceo-photo.jpg";

const stats = [
  { icon: Users, value: "15+", label: "Team Members", color: "green" as const },
  { icon: Calendar, value: "6+", label: "Years Experience", color: "blue" as const },
  { icon: Award, value: "50+", label: "Projects Delivered", color: "gold" as const },
  { icon: Award, value: "5", label: "Industry Awards", color: "purple" as const },
];

const environmentalStats = [
  { icon: Zap, value: "100%", label: "Electric Fleet" },
  { icon: Leaf, value: "Zero", label: "Emissions" },
  { icon: TreePine, value: "500+", label: "CO₂ Saved" },
];

const values = [
  { icon: Leaf, title: "Sustainability", description: "Eco-conscious operations at every level of our business", color: "green" as const },
  { icon: Target, title: "Vision 2030", description: "Aligned with Saudi Arabia's transformative future goals", color: "gold" as const },
  { icon: Award, title: "Excellence", description: "Premium quality products and world-class services", color: "blue" as const },
  { icon: Heart, title: "Community", description: "Driving progress and empowering our community", color: "purple" as const },
  { icon: Lightbulb, title: "Innovation", description: "Pioneering cutting-edge mobility technologies", color: "teal" as const },
  { icon: Shield, title: "Integrity", description: "Transparent and ethical business practices", color: "green" as const },
];

const colorStyles = {
  green: {
    bg: "bg-gradient-to-br from-eco-green/20 to-eco-green/5",
    iconBg: "bg-gradient-to-br from-eco-green to-eco-green-light",
    text: "text-eco-green",
    border: "border-eco-green/20 hover:border-eco-green/40",
    glow: "group-hover:shadow-[0_0_30px_hsl(var(--eco-green)/0.3)]",
  },
  blue: {
    bg: "bg-gradient-to-br from-sky-blue/20 to-sky-blue/5",
    iconBg: "bg-gradient-to-br from-sky-blue to-sky-blue-light",
    text: "text-sky-blue",
    border: "border-sky-blue/20 hover:border-sky-blue/40",
    glow: "group-hover:shadow-[0_0_30px_hsl(var(--sky-blue)/0.3)]",
  },
  gold: {
    bg: "bg-gradient-to-br from-primary/20 to-amber-glow/5",
    iconBg: "bg-gradient-to-br from-primary to-amber-glow",
    text: "text-primary",
    border: "border-primary/20 hover:border-primary/40",
    glow: "group-hover:shadow-[0_0_30px_hsl(var(--primary)/0.3)]",
  },
  purple: {
    bg: "bg-gradient-to-br from-purple/20 to-purple/5",
    iconBg: "bg-gradient-to-br from-purple to-rose",
    text: "text-purple",
    border: "border-purple/20 hover:border-purple/40",
    glow: "group-hover:shadow-[0_0_30px_hsl(var(--purple)/0.3)]",
  },
  teal: {
    bg: "bg-gradient-to-br from-teal-bright/20 to-teal-bright/5",
    iconBg: "bg-gradient-to-br from-teal-bright to-eco-green",
    text: "text-teal-bright",
    border: "border-teal-bright/20 hover:border-teal-bright/40",
    glow: "group-hover:shadow-[0_0_30px_hsl(var(--teal-bright)/0.3)]",
  },
};

const timeline = [
  { year: "2019", title: "Study & Analysis", description: "Conducted comprehensive market research and analyzed sustainable mobility opportunities in Saudi Arabia", icon: BookOpen },
  { year: "2020", title: "Ideation", description: "Developed innovative concepts and business models for eco-friendly transportation solutions", icon: Lightbulb },
  { year: "2021", title: "Pilot & Retest", description: "Launched pilot programs to test and refine our mobility solutions based on real-world feedback", icon: Target },
  { year: "2022", title: "Foundation", description: "Officially established Sustainable Development LLC in Jeddah as House of Mobility", icon: Building2 },
  { year: "2023", title: "Pre-Seeding", description: "Secured initial investments and built strategic partnerships to scale operations", icon: Rocket },
  { year: "2024", title: "First Major Contract", description: "Won our first B2G contract, supplying electric mobility solutions to government entities", icon: Award },
  { year: "2025", title: "Brand Expansion", description: "Launched LEMO and SOHOB brands, expanding into leasing mobility and eco-products", icon: TrendingUp },
];

const impactStats = [
  { value: "500+", label: "Tons CO₂ Prevented", description: "Annual carbon emissions avoided through our electric fleet", color: "green" as const },
  { value: "24,000+", label: "Trees Equivalent", description: "Environmental impact measured in tree absorption capacity", color: "teal" as const },
  { value: "1.2M+", label: "Clean Kilometers", description: "Zero-emission kilometers traveled by our fleet", color: "blue" as const },
  { value: "100%", label: "Electric Fleet", description: "All our mobility solutions run on clean energy", color: "gold" as const },
];

const sdgGoals = [
  { number: 7, title: "Clean Energy", progress: 95, color: "from-yellow-500 to-amber-500" },
  { number: 11, title: "Sustainable Cities", progress: 88, color: "from-orange-500 to-amber-600" },
  { number: 13, title: "Climate Action", progress: 92, color: "from-green-600 to-emerald-500" },
];

const achievements = [
  { title: "Vision 2030 Partner", description: "Recognized contributor to Saudi sustainability goals" },
  { title: "Carbon Neutral Operations", description: "Net-zero emissions across all business operations" },
  { title: "Year-over-Year Growth", description: "35% increase in sustainable deployments" },
];

const About = () => {
  return (
    <div className="min-h-screen bg-background">
      <PageSEO
        title="About SDC — Sustainable Mobility KSA"
        description="SDC (تطوير الاستدامة) — Jeddah-based micro mobility company delivering sustainable electric transportation across Saudi Arabia since 2019."
        keywords="SDC Gulf, تطوير الاستدامة, sustainable mobility company Jeddah, micro mobility company Saudi Arabia, about SDC, Vision 2030 partner"
      />
      <Navbar />
      
      {/* Vibrant Hero Section */}
      <PageHero
        title="Pioneering Green Mobility"
        subtitle="About Sustainable Development"
        description="House of Mobility — Smart, Fast, Noiseless, Green. We are the leading provider of sustainable E-Transportation solutions in Saudi Arabia."
        variant="vibrant"
      >
        <div className="flex flex-wrap gap-4 justify-center">
          <Button 
            size="xl" 
            asChild
            className="bg-gradient-to-r from-eco-green via-teal-bright to-eco-green-light hover:from-eco-green-light hover:via-teal-bright hover:to-eco-green text-white font-semibold shadow-lg shadow-eco-green/25 hover:shadow-xl hover:shadow-eco-green/30 transition-all duration-300 hover:scale-105"
          >
            <Link to="/services">
              Our Services
              <ArrowRight className="ml-2 w-4 h-4" />
            </Link>
          </Button>
          <Button variant="heroOutline" size="xl" asChild>
            <a href="https://www.storydoc.com/8a850033fc6e9e2c39d9b05507e495f1/ae1b39c2-bb70-4fe9-98c7-41cd98de7bb3/66b817b24caafe32ec8472d6" target="_blank" rel="noopener noreferrer">
              Catalogue
            </a>
          </Button>
        </div>
      </PageHero>

      <Breadcrumbs />

      {/* Stats Grid Section */}
      <section className="py-16 relative overflow-hidden">
        {/* Animated background elements */}
        <div className="absolute -left-40 top-1/2 -translate-y-1/2 w-80 h-80 rounded-full bg-eco-green/10 blur-3xl animate-pulse" />
        <div className="absolute -right-40 top-1/2 -translate-y-1/2 w-80 h-80 rounded-full bg-sky-blue/10 blur-3xl animate-pulse" style={{ animationDelay: "1s" }} />
        
        <div className="container mx-auto px-6 relative z-10">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
            {stats.map((stat, index) => {
              const style = colorStyles[stat.color];
              return (
                <div
                  key={index}
                  className={`group p-6 rounded-2xl ${style.bg} border ${style.border} backdrop-blur-sm text-center transition-all duration-500 hover:-translate-y-2 ${style.glow}`}
                >
                  <div className={`w-14 h-14 mx-auto mb-4 rounded-xl ${style.iconBg} flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform duration-300`}>
                    <stat.icon className="w-7 h-7 text-white" />
                  </div>
                  <div className={`text-3xl md:text-4xl font-bold ${style.text} mb-1`}>{stat.value}</div>
                  <div className="text-sm text-muted-foreground">{stat.label}</div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Mission & Vision with CEO Photo */}
      <section className="py-20 relative overflow-hidden">
        {/* Floating particles */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          {[...Array(6)].map((_, i) => (
            <div
              key={i}
              className="absolute w-2 h-2 rounded-full bg-eco-green/30"
              style={{
                left: `${15 + i * 15}%`,
                top: `${20 + (i % 3) * 25}%`,
                animation: `float ${3 + i * 0.5}s ease-in-out infinite`,
                animationDelay: `${i * 0.3}s`,
              }}
            />
          ))}
        </div>
        
        <div className="container mx-auto px-6 relative z-10">
          <VibrantSectionHeader
            badge="Our Purpose"
            title="Mission & Vision"
            badgeColor="gold"
          />
          
          <div className="grid lg:grid-cols-2 gap-12 items-start">
            {/* Mission & Vision Cards */}
            <div className="space-y-6">
              <div className="group p-8 rounded-2xl bg-gradient-to-br from-primary/10 to-amber-glow/5 border border-primary/20 hover:border-primary/40 transition-all duration-500 hover:-translate-y-1 group-hover:shadow-[0_0_30px_hsl(var(--primary)/0.2)]">
                <div className="flex items-center gap-4 mb-4">
                  <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-primary to-amber-glow flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform">
                    <Target className="w-7 h-7 text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-foreground">Our Mission</h3>
                </div>
                <p className="text-muted-foreground leading-relaxed">
                  To lead Saudi Arabia's transition to sustainable mobility by providing innovative, eco-friendly transportation solutions.
                </p>
              </div>
              
              <div className="group p-8 rounded-2xl bg-gradient-to-br from-eco-green/10 to-teal-bright/5 border border-eco-green/20 hover:border-eco-green/40 transition-all duration-500 hover:-translate-y-1 group-hover:shadow-[0_0_30px_hsl(var(--eco-green)/0.2)]">
                <div className="flex items-center gap-4 mb-4">
                  <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-eco-green to-teal-bright flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform">
                    <Eye className="w-7 h-7 text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-foreground">Our Vision</h3>
                </div>
                <p className="text-muted-foreground leading-relaxed">
                  To be the foremost catalyst for sustainable transformation in the Gulf region, aligned with Vision 2030.
                </p>
              </div>
              
              {/* Environmental Quick Stats */}
              <div className="grid grid-cols-3 gap-4">
                {environmentalStats.map((stat, index) => (
                  <div key={index} className="text-center p-4 rounded-xl bg-gradient-to-br from-eco-green/15 to-teal-bright/5 border border-eco-green/20 backdrop-blur-sm hover:border-eco-green/40 transition-all duration-300">
                    <stat.icon className="w-6 h-6 text-eco-green mx-auto mb-2" />
                    <div className="text-xl font-bold text-eco-green">{stat.value}</div>
                    <div className="text-xs text-muted-foreground">{stat.label}</div>
                  </div>
                ))}
              </div>
            </div>
            
            {/* CEO Photo with overlays */}
            <div className="relative">
              <div className="relative rounded-2xl overflow-hidden border border-border/30 shadow-2xl">
                <img 
                  src={ceoPhoto} 
                  alt="SDC Gulf Leadership" 
                  className="w-full h-auto object-cover rounded-2xl"
                />
                {/* Overlay badges */}
                <div className="absolute top-4 right-4 p-4 rounded-xl bg-gradient-to-br from-eco-green to-teal-bright text-white shadow-lg">
                  <Leaf className="w-6 h-6 mx-auto mb-1" />
                  <div className="text-xl font-bold">100%</div>
                  <div className="text-xs">Sustainable</div>
                </div>
                <div className="absolute bottom-4 left-4 p-4 rounded-xl bg-gradient-to-br from-primary to-amber-glow backdrop-blur-sm text-primary-foreground shadow-lg">
                  <div className="text-sm font-medium">Vision 2030</div>
                  <div className="text-xs opacity-80">Aligned Partner</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Core Values */}
      <section className="py-20 relative overflow-hidden">
        {/* Animated background */}
        <div className="absolute inset-0 bg-gradient-to-r from-eco-green/5 via-transparent to-sky-blue/5" />
        <div className="absolute -left-40 bottom-0 w-80 h-80 rounded-full bg-purple/10 blur-3xl animate-pulse" />
        <div className="absolute -right-40 top-0 w-80 h-80 rounded-full bg-primary/10 blur-3xl animate-pulse" style={{ animationDelay: "1.5s" }} />
        
        <div className="container mx-auto px-6 relative z-10">
          <VibrantSectionHeader
            badge="What Drives Us"
            title="Our Core Values"
            badgeColor="green"
          />
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {values.map((value, index) => {
              const style = colorStyles[value.color];
              return (
                <div
                  key={index}
                  className={`group p-6 rounded-2xl ${style.bg} border ${style.border} backdrop-blur-sm transition-all duration-500 hover:-translate-y-2 ${style.glow}`}
                >
                  <div className={`w-14 h-14 rounded-xl ${style.iconBg} flex items-center justify-center mb-4 shadow-lg group-hover:scale-110 group-hover:rotate-3 transition-all duration-300`}>
                    <value.icon className="w-7 h-7 text-white" />
                  </div>
                  <h3 className={`text-lg font-bold ${style.text} mb-2`}>{value.title}</h3>
                  <p className="text-sm text-muted-foreground">{value.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Journey Timeline */}
      <section className="py-20 relative overflow-hidden">
        <div className="container mx-auto px-6 relative z-10">
          <VibrantSectionHeader
            badge="Our Story"
            title="The Journey So Far"
            badgeColor="blue"
          />
          
          {/* Horizontal Timeline for Desktop */}
          <div className="hidden lg:block">
            <div className="relative">
              {/* Timeline Line */}
              <div className="absolute top-16 left-0 right-0 h-1 bg-gradient-to-r from-eco-green via-teal-bright via-sky-blue via-primary to-purple rounded-full" />
              
              <div className="grid grid-cols-7 gap-4">
                {timeline.map((item, index) => {
                  const colors = ["green", "teal", "blue", "gold", "purple", "green", "blue"] as const;
                  const style = colorStyles[colors[index]];
                  return (
                    <div key={index} className="relative text-center group">
                      {/* Year Badge */}
                      <div className={`inline-flex items-center justify-center w-14 h-14 rounded-full ${style.iconBg} text-white font-bold text-sm mb-2 relative z-10 shadow-lg group-hover:scale-110 transition-transform duration-300`}>
                        {item.year.slice(-2)}
                      </div>
                      <div className={`${style.text} font-bold text-sm mb-4`}>{item.year}</div>
                      
                      {/* Content Card */}
                      <div className={`p-4 rounded-xl ${style.bg} border ${style.border} backdrop-blur-sm transition-all duration-500 hover:-translate-y-1 ${style.glow}`}>
                        <item.icon className={`w-6 h-6 ${style.text} mx-auto mb-2`} />
                        <h3 className="font-bold text-sm text-foreground mb-1">{item.title}</h3>
                        <p className="text-xs text-muted-foreground line-clamp-3">{item.description}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
          
          {/* Vertical Timeline for Mobile */}
          <div className="lg:hidden space-y-6">
            {timeline.map((item, index) => {
              const colors = ["green", "teal", "blue", "gold", "purple", "green", "blue"] as const;
              const style = colorStyles[colors[index]];
              return (
                <div key={index} className="flex gap-4">
                  <div className="flex flex-col items-center">
                    <div className={`w-12 h-12 rounded-full ${style.iconBg} text-white font-bold text-xs flex items-center justify-center shadow-lg`}>
                      {item.year.slice(-2)}
                    </div>
                    {index < timeline.length - 1 && <div className="w-0.5 flex-1 bg-gradient-to-b from-eco-green to-sky-blue mt-2" />}
                  </div>
                  <div className="flex-1 pb-6">
                    <div className={`p-4 rounded-xl ${style.bg} border ${style.border} backdrop-blur-sm`}>
                      <div className={`${style.text} font-bold text-sm mb-1`}>{item.year}</div>
                      <h3 className="font-bold text-foreground mb-1">{item.title}</h3>
                      <p className="text-sm text-muted-foreground">{item.description}</p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Environmental Impact Report */}
      <section className="py-20 relative overflow-hidden">
        {/* Animated gradient background */}
        <div className="absolute inset-0 bg-gradient-to-br from-eco-green/10 via-background to-sky-blue/10" />
        <div className="absolute -left-40 top-1/3 w-96 h-96 rounded-full bg-eco-green/15 blur-3xl animate-pulse" />
        <div className="absolute -right-40 bottom-1/3 w-96 h-96 rounded-full bg-sky-blue/15 blur-3xl animate-pulse" style={{ animationDelay: "1s" }} />
        
        {/* Floating particles */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          {[...Array(8)].map((_, i) => (
            <div
              key={i}
              className={`absolute w-2 h-2 rounded-full ${i % 2 === 0 ? 'bg-eco-green/40' : 'bg-sky-blue/40'}`}
              style={{
                left: `${10 + i * 12}%`,
                top: `${15 + (i % 4) * 20}%`,
                animation: `float ${3 + i * 0.3}s ease-in-out infinite`,
                animationDelay: `${i * 0.2}s`,
              }}
            />
          ))}
        </div>
        
        <div className="container mx-auto px-6 relative z-10">
          <VibrantSectionHeader
            badge="2024 Impact Report"
            title="Our Environmental Impact"
            description="Transparent reporting on our contributions to a sustainable future"
            badgeColor="green"
          />
          
          {/* Impact Stats Grid */}
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
            {impactStats.map((stat, index) => {
              const style = colorStyles[stat.color];
              return (
                <div
                  key={index}
                  className={`group p-6 rounded-2xl ${style.bg} border ${style.border} backdrop-blur-sm text-center transition-all duration-500 hover:-translate-y-2 ${style.glow}`}
                >
                  <div className={`text-3xl md:text-4xl font-bold ${style.text} mb-2`}>{stat.value}</div>
                  <div className="font-semibold text-foreground mb-2">{stat.label}</div>
                  <p className="text-sm text-muted-foreground">{stat.description}</p>
                </div>
              );
            })}
          </div>
          
          <div className="grid lg:grid-cols-2 gap-8">
            {/* UN SDG Contributions */}
            <div className="p-8 rounded-2xl bg-gradient-to-br from-sky-blue/10 to-purple/5 border border-sky-blue/20 backdrop-blur-sm">
              <h3 className="text-xl font-bold text-foreground mb-6 flex items-center gap-2">
                <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-sky-blue to-purple flex items-center justify-center">
                  <Globe className="w-5 h-5 text-white" />
                </div>
                UN SDG Contributions
              </h3>
              <div className="space-y-6">
                {sdgGoals.map((goal, index) => (
                  <div key={index}>
                    <div className="flex justify-between items-center mb-2">
                      <span className="font-medium text-foreground">SDG {goal.number}: {goal.title}</span>
                      <span className="text-sky-blue font-bold">{goal.progress}%</span>
                    </div>
                    <div className="w-full bg-secondary/50 rounded-full h-3 overflow-hidden">
                      <div
                        className={`h-3 rounded-full bg-gradient-to-r ${goal.color} transition-all duration-500`}
                        style={{ width: `${goal.progress}%` }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>
            
            {/* Key Achievements */}
            <div className="p-8 rounded-2xl bg-gradient-to-br from-primary/10 to-amber-glow/5 border border-primary/20 backdrop-blur-sm">
              <h3 className="text-xl font-bold text-foreground mb-6 flex items-center gap-2">
                <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-primary to-amber-glow flex items-center justify-center">
                  <Award className="w-5 h-5 text-white" />
                </div>
                Key Achievements
              </h3>
              <div className="space-y-4">
                {achievements.map((achievement, index) => (
                  <div key={index} className="flex gap-4 p-4 rounded-xl bg-gradient-to-r from-eco-green/10 to-transparent border border-eco-green/20">
                    <CheckCircle2 className="w-6 h-6 text-eco-green flex-shrink-0 mt-0.5" />
                    <div>
                      <h4 className="font-bold text-foreground">{achievement.title}</h4>
                      <p className="text-sm text-muted-foreground">{achievement.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Vision 2030 Commitment */}
      <section className="py-20 relative overflow-hidden">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto p-8 rounded-2xl bg-gradient-to-r from-eco-green/10 via-teal-bright/10 to-sky-blue/10 border border-eco-green/20 backdrop-blur-sm relative overflow-hidden">
            {/* Decorative gradient orbs */}
            <div className="absolute -top-20 -left-20 w-40 h-40 rounded-full bg-eco-green/20 blur-3xl" />
            <div className="absolute -bottom-20 -right-20 w-40 h-40 rounded-full bg-sky-blue/20 blur-3xl" />
            
            <div className="text-center relative z-10">
              <h3 className="text-2xl md:text-3xl font-bold text-foreground mb-4">Committed to Vision 2030</h3>
              <p className="text-muted-foreground mb-8 max-w-2xl mx-auto">
                Every project we undertake contributes to the Kingdom's sustainability objectives. Together with our partners, we're building a greener, more efficient future.
              </p>
              <div className="flex flex-wrap justify-center gap-4">
                <div className="px-6 py-3 rounded-full bg-gradient-to-r from-primary/20 to-amber-glow/20 border border-primary/30 hover:scale-105 transition-transform cursor-default">
                  <span className="text-primary font-medium">Zero Emissions Goal</span>
                </div>
                <div className="px-6 py-3 rounded-full bg-gradient-to-r from-eco-green/20 to-teal-bright/20 border border-eco-green/30 hover:scale-105 transition-transform cursor-default">
                  <span className="text-eco-green font-medium">Sustainable Development</span>
                </div>
                <div className="px-6 py-3 rounded-full bg-gradient-to-r from-sky-blue/20 to-purple/20 border border-sky-blue/30 hover:scale-105 transition-transform cursor-default">
                  <span className="text-sky-blue font-medium">Green Economy</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Headquarters */}
      <section className="py-20 relative overflow-hidden">
        {/* Animated background */}
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-secondary/30 to-transparent" />
        <div className="absolute -right-40 top-1/2 -translate-y-1/2 w-80 h-80 rounded-full bg-primary/10 blur-3xl animate-pulse" />
        
        <div className="container mx-auto px-6 relative z-10">
          <VibrantSectionHeader
            badge="Headquarters"
            title="Based in Jeddah"
            badgeColor="gold"
          />
          
          <div className="max-w-2xl mx-auto">
            <div className="p-8 rounded-2xl bg-gradient-to-br from-primary/10 to-amber-glow/5 border border-primary/20 backdrop-blur-sm text-center group hover:border-primary/40 transition-all duration-500 hover:shadow-[0_0_40px_hsl(var(--primary)/0.2)]">
              <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-primary to-amber-glow flex items-center justify-center mx-auto mb-6 shadow-lg group-hover:scale-110 transition-transform duration-300">
                <MapPin className="w-10 h-10 text-white" />
              </div>
              <p className="text-muted-foreground mb-4">
                Strategically located in Jeddah, serving clients across the Kingdom and the Gulf region.
              </p>
              <h3 className="text-xl font-bold text-foreground mb-1">Sustainable Development LLC</h3>
              <p className="text-muted-foreground mb-6">Jeddah 23511, Saudi Arabia</p>
              <div className="flex flex-wrap justify-center gap-4">
                <Button 
                  asChild
                  className="bg-gradient-to-r from-eco-green via-teal-bright to-eco-green-light hover:from-eco-green-light hover:via-teal-bright hover:to-eco-green text-white font-semibold shadow-lg shadow-eco-green/25 hover:shadow-xl hover:shadow-eco-green/30 transition-all duration-300 hover:scale-105"
                >
                  <Link to="/contact">Contact Us</Link>
                </Button>
                <Button variant="heroOutline" asChild>
                  <Link to="/careers">Join Our Team</Link>
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>

      <PartnerLogos title="Our Partners & Clients" />

      <CTASection
        title="Ready to Go Green?"
        description="Partner with us for sustainable mobility solutions."
        primaryButtonText="Get Started"
        primaryButtonLink="/contact"
        secondaryButtonText="View Careers"
        secondaryButtonLink="/careers"
      />

      <Footer />
    </div>
  );
};

export default About;
